import { useEffect, useRef } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { removeToast, useToasts } from '../services/Toasts'
import { ToastType } from '../common/common'

// Per-type background + text colour. Mirrors the rules that used to live in
// ta-legacy.css under `.toast-container.toast-*`.
const TYPE_STYLE: Record<ToastType, { bg: string; fg: string }> = {
  success: { bg: 'rgb(50, 204, 153)', fg: '#fff' },
  error: { bg: '#B93B46', fg: '#fff' },
  warn: { bg: '#FECC27', fg: '#fff' },
  info: { bg: '#fff', fg: 'rgb(59, 59, 59)' },
}

interface ToastItemProps {
  id: string
  type: ToastType
  message: string
  onRemove: (id: string) => void
}

function ToastItem({ id, type, message, onRemove }: ToastItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { bg, fg } = TYPE_STYLE[type]

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Trigger fade-in on next frame
    const raf = requestAnimationFrame(() => {
      el.style.opacity = '1'
    })
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <Box
      ref={ref}
      sx={{
        mb: '10px',
        bgcolor: bg,
        color: fg,
        boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
        width: 'fit-content',
        minWidth: 300,
        borderRadius: '10px',
        p: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 30,
        opacity: 0,
        transition: 'opacity 0.25s ease',
      }}
    >
      <Typography sx={{ m: 0, fontSize: 15, color: fg }}>{message}</Typography>
      <IconButton
        aria-label="Close"
        onClick={() => onRemove(id)}
        sx={{
          ml: 1,
          height: 30,
          width: 30,
          color: fg,
          borderRadius: '50%',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.096)' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

export default function ToastMessages() {
  const toasts = useToasts()

  return (
    <Box
      sx={{
        position: 'fixed',
        width: 400,
        maxHeight: '98vh',
        overflow: 'hidden',
        overflowY: 'auto',
        bottom: 0,
        right: 0,
        alignItems: 'end',
        display: 'flex',
        flexDirection: 'column',
        py: '1vh',
        px: '2rem',
        zIndex: 1000,
        // The container itself is non-interactive - pointer events live
        // on the individual toast cards so clicks don't bleed onto the
        // app behind the empty parts of the stack.
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {toasts.map(toast => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          onRemove={removeToast}
        />
      ))}
    </Box>
  )
}
